import React, { useRef, useState } from 'react';
import '../styles/CityOrderControl.css';

type OrderControlProps<T extends string> = {
  title: string;
  order: readonly T[];
  visibleItems?: readonly T[];
  label?: (item: T) => string;
  onChange: (order: T[]) => void;
  itemAttribute?: string;
};

export function OrderControl<T extends string>({ title, order, visibleItems = order, label = item => item, onChange, itemAttribute = 'data-order-item' }: OrderControlProps<T>) {
  const [drag, setDrag] = useState<{ name: T; target: T }>();
  const list = useRef<HTMLOListElement>(null);
  const visible = visibleItems;
  const move = (name: T, target: T) => {
    if (name === target) return;
    // Replace visible slots only, preserving the positions of hidden entries.
    const next = visible.filter(item => item !== name);
    next.splice(visible.indexOf(target), 0, name);
    let index = 0;
    onChange(order.map(item => visible.includes(item) ? next[index++] : item));
  };
  const finish = (event: React.PointerEvent, cancelled = false) => {
    if (drag && !cancelled) move(drag.name, drag.target);
    setDrag(undefined);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  return <fieldset className="settings-field city-order-control" onKeyDown={event => {
    if (event.key === 'Escape' && drag) { setDrag(undefined); event.stopPropagation(); }
  }}>
    <legend>{title}</legend>
    <ol ref={list}>
      {visible.map((name, index) => <li key={name} {...{ [itemAttribute]: name }}
        className={drag?.target === name ? 'city-order-target' : undefined}>
        <button type="button" className="city-order-handle" aria-label={`Drag ${label(name)}`} aria-pressed={drag?.name === name}
          onPointerDown={event => {
            if (event.button !== 0 || !event.isPrimary) return;
            event.currentTarget.setPointerCapture?.(event.pointerId);
            setDrag({ name, target: name });
          }} onPointerMove={event => {
            if (!drag) return;
            const rows = Array.from(list.current?.children ?? []) as HTMLElement[];
            const target = rows.find(row => { const box = row.getBoundingClientRect(); return event.clientY >= box.top && event.clientY <= box.bottom; });
            if (target) setDrag({ ...drag, target: target.getAttribute(itemAttribute) as T });
          }} onPointerUp={event => finish(event)} onPointerCancel={event => finish(event, true)}
          onLostPointerCapture={event => finish(event, true)}>⠿</button>
        <span>{label(name)}</span>
        <button type="button" aria-label={`Move ${label(name)} up`} disabled={index === 0}
          onClick={() => move(name, visible[index - 1])}>↑</button>
        <button type="button" aria-label={`Move ${label(name)} down`} disabled={index === visible.length - 1}
          onClick={() => move(name, visible[index + 1])}>↓</button>
      </li>)}
    </ol>
    <span className="city-order-status" aria-live="polite">{drag ? `Moving ${label(drag.name)} to position ${visible.indexOf(drag.target) + 1}` : ''}</span>
  </fieldset>;
}
